import { EmptyState, TableWithGroupingsLoadingSkeleton } from '@repo/ui';
import { ProfessionalsWorkJourneyOrganization } from 'domain/models';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Row } from 'reactstrap';

import { normalizeText } from '../helpers';
import { OrganizationJourneyGroup } from '../organization-journey-group';

const ORGANIZATION_DEFAULT_OPEN = false;

interface IProps {
  organizations: ProfessionalsWorkJourneyOrganization[];
  loading: boolean;
}

export const WorkJourneyGroupedList = ({ organizations, loading }: IProps) => {
  const [search, setSearch] = useState('');

  const [openStates, setOpenStates] = useState<Record<string, boolean>>({});

  const getOrganizationKey = (organizationIndex: number) =>
    `organization-${organizationIndex}`;

  const filteredOrganizations = useMemo(() => {
    const term = normalizeText(search);

    if (!term) {
      return organizations;
    }

    return organizations.reduce<ProfessionalsWorkJourneyOrganization[]>(
      (accumulator, organization) => {
        const professionals = organization.professionals || [];

        if (normalizeText(organization.name).includes(term)) {
          accumulator.push(organization);
          return accumulator;
        }

        const matchedProfessionals = professionals.filter((professional) =>
          normalizeText(professional.name).includes(term),
        );

        if (matchedProfessionals.length > 0) {
          accumulator.push({
            ...organization,
            professionals: matchedProfessionals,
          });
        }

        return accumulator;
      },
      [],
    );
  }, [organizations, search]);

  const isOpen = useCallback(
    (key: string) => openStates[key] ?? ORGANIZATION_DEFAULT_OPEN,
    [openStates],
  );

  const toggleKey = useCallback((key: string) => {
    setOpenStates((prev) => ({
      ...prev,
      [key]: !(prev[key] ?? ORGANIZATION_DEFAULT_OPEN),
    }));
  }, []);

  const handleToggleAll = useCallback(
    (shouldOpen: boolean) => {
      const nextOpenStates: Record<string, boolean> = {};

      filteredOrganizations.forEach((_, organizationIndex) => {
        nextOpenStates[getOrganizationKey(organizationIndex)] = shouldOpen;
      });

      setOpenStates(nextOpenStates);
    },
    [filteredOrganizations],
  );

  const isEverythingOpen = useMemo(
    () =>
      filteredOrganizations.length > 0 &&
      filteredOrganizations.every(
        (_, organizationIndex) =>
          openStates[getOrganizationKey(organizationIndex)] ??
          ORGANIZATION_DEFAULT_OPEN,
      ),
    [filteredOrganizations, openStates],
  );

  const hasData = filteredOrganizations.length > 0;

  useEffect(() => {
    setOpenStates({});
  }, [filteredOrganizations]);

  return (
    <>
      <Row className="g-3 align-items-center mb-3">
        <Col xl={4} lg={5} md={6} sm={8} xs={12}>
          <div className="search-box">
            <input
              type="text"
              id="search"
              value={search}
              className="form-control search"
              placeholder="Pesquisar profissional..."
              onChange={(event) => setSearch(event.target.value)}
            />
            <i className="ri-search-line search-icon"></i>
          </div>
        </Col>

        <Col className="d-flex align-items-center justify-content-end gap-3">
          {!loading && hasData && (
            <Button
              size="sm"
              color="light"
              onClick={() => handleToggleAll(!isEverythingOpen)}
            >
              {isEverythingOpen ? (
                <>
                  <i className="ri-contract-up-down-line me-1"></i>
                  Recolher todos
                </>
              ) : (
                <>
                  <i className="ri-expand-up-down-line me-1"></i>
                  Expandir todos
                </>
              )}
            </Button>
          )}
        </Col>
      </Row>

      {loading && (
        <TableWithGroupingsLoadingSkeleton
          numberOfGroups={3}
          rowsPerGroup={3}
        />
      )}

      {!loading && !hasData && (
        <EmptyState
          variant="filter"
          title="Nenhum registro encontrado"
          description="Não há jornada de trabalho para os filtros aplicados."
          size={100}
          paddingY={3}
        />
      )}

      {!loading &&
        hasData &&
        filteredOrganizations.map((organization, organizationIndex) => (
          <OrganizationJourneyGroup
            organization={organization}
            key={`${organizationIndex}-${organization.id || ''}`}
            isOpen={isOpen(getOrganizationKey(organizationIndex))}
            onToggle={() => toggleKey(getOrganizationKey(organizationIndex))}
          />
        ))}
    </>
  );
};
